import { useMemo, lazy, Suspense } from 'react';
import { getConditionsMenuItems } from "../serviceAndConditionsMenu/conditionsMenuItems";
import { Link } from "react-router";
import { cls } from "../../../NUI/utils";

/**
 * Footer Content is rendered with static HTML for SSR/SEO purposes.
 * Non-dumb components (those that use `app`, connect to APIs, or require
 * imports to complex models/components) should be lazy loaded so static
 * HTML can render without JavaScript dependencies.
 */
const SiteInformation = lazy(() => import("./SiteInformation"));


const showPrivacySettings = () => window?.ketch && window.ketch('showExperience', { displayHint: 'experiences.consent.modal' });

const MAX_ITEMS = 15;
const INSTANTSCRIPTS_MENU_ITEMS = [
	{ title: "Join our Doctor Team", href: "/doctor-careers" },
	{ title: "Health Hub", href: "/blog" },
	{ title: "In the media", href: "https://www.instantscripts.com.au/media_release" },
	{ title: "Pricing", href: "/pricing" },
	{ title: "Our Story", href: "/about-us" },
	{
		title: "Privacy",
		onClick: () => app.trigger(app.events.SHOW_POPUP, {
			pt: "cx_txt",
			txt: "Privacy",
			pn: "privacy-policy",
		}),
	},
	{
		title: "Privacy Settings",
		onClick: () => showPrivacySettings(),
	},
	{
		title: "Customer T&Cs",
		onClick: () =>
			app.trigger(app.events.SHOW_POPUP, {
				pt: "cx_txt",
				txt: "Customer Terms and Conditions",
				pn: "customer-terms-and-conditions",
			}),
	},
	{
		title: "Platform T&Cs",
		onClick: () =>
			app.trigger(app.events.SHOW_POPUP, {
				pt: "cx_txt",
				txt: "Platform Terms and Conditions",
				pn: "platform-terms-and-conditions",
			}),
	},
	{ title: "Wesfarmers Telehealth Code of Practice", href: "/code-of-practice" },
	{ title: "Sitemap", href: "/sitemap" },
];

const PROMOTION_MENU_ITEMS = [
	{ title: "Pulse Rewards", href: "/pulse-rewards" },
	{ title: "OnePass", href: "/onepass" },
	{ title: "Refer a friend", href: "/refer-a-friend" },
];

const HELP_MENU_ITEMS = [
	{ title: "Help center", href: "https://help.instantscripts.com.au/en" },
	{ title: "Contact us", href: "/contact-us" },
];

const WORK_WITH_US_MENU_ITEMS = [{ title: "Pharmacies", href: "/pharmacy-registration" }];

const SOCIAL_MENU_ITEMS = [
	{ title: <img src="/images/logos/facebook.svg" alt="Facebook" className="logo h-6 w-auto" />, href: "https://www.facebook.com/InstantScripts/" },
	{ title: <img src="/images/logos/instagram.svg" alt="Instagram" className="logo h-6 w-auto" />, href: "https://www.instagram.com/instantscripts/" },
	{ title: <img src="/images/logos/tiktok.svg" alt="TikTok" className="logo h-6 w-auto" />, href: "https://www.tiktok.com/@instantscripts" }
];

const IS_PHARMACY_MENU_ITEMS = [
	{ title: "Locations", href: "/pharmacy-health-hub" },
	{ title: "Privacy", href: "/phh/privacy" },
];

const INSTANT_SCRIPT_LOGO = (
	<img src="/images/logos/instant-scripts-logo-IS2-white.svg" alt="InstantScripts" className="logo h-[26px] w-auto" />
);

const IS_PHARMACY_LOGO = (
	<img src="/images/logos/IS-Pharmacy-white.svg" alt="InstantScripts Pharmacy" className="logo h-[26px] w-auto" />
);
const useFooterLists = (serviceMenu, showKetch, wl_link) => {
	
	const instantScriptLinks = useMemo(() => {
		let items = INSTANTSCRIPTS_MENU_ITEMS;
		if (!showKetch) {
			items = items.filter(item => item.title !== "Privacy Settings");
		}
		return { title: INSTANT_SCRIPT_LOGO, items };
	}, [showKetch]);

    const services = useMemo(() => {
        return {title : 'Services', items : [...serviceMenu.flatMap(({items}) => items).filter((el) => el?.footerOrder).sort((a, b) => a.footerOrder - b.footerOrder), { title: "Doctors Note", href: '/doctors-note'}] }
    }, [])

	const conditionsMenu = useMemo(() => {
		return {
			title: "Top Conditions",
			items: getConditionsMenuItems({wl_link}).common
				.map(({ url, n, footerOrder }) => ({ href: url, title: n, footerOrder }))
				.sort((a, b) => a.footerOrder - b.footerOrder),
		};
	}, [wl_link]);

	const promotions = useMemo(() => {
		return { title: "Promotions", items: PROMOTION_MENU_ITEMS };
	}, []);

	const workWithUs = useMemo(() => {
		return { title: "Work With Us", items: WORK_WITH_US_MENU_ITEMS };
	}, []);

	const help = useMemo(() => {
		return { title: "Help", items: HELP_MENU_ITEMS };
	}, []);

	const socialLinks = useMemo(() => {
		return { title: <>Follow us<span className="md:hidden">:</span></>, items: SOCIAL_MENU_ITEMS };
	}, []);

	const ISPharmacy = useMemo(() => {
		return { title: IS_PHARMACY_LOGO, items: IS_PHARMACY_MENU_ITEMS };
	}, []);

	return { services, ISPharmacy, workWithUs, promotions, conditionsMenu, instantScriptLinks, help, socialLinks };
};

const FooterLinkItem = ({ href, title, target, onClick }) => {
	const isExternalLink = href && href.startsWith("http");
	const className =
		"no-underline text-blue-300 text-sm cursor-pointer font-thin hover:text-white transition duration-300";
	return (
		<div className="mb-1">
			{!href ? (
				<button className={className} onClick={onClick} type="button">
					{title}
				</button>
			) : isExternalLink ? (
				<a className={className} onClick={onClick} href={href} target={target ?? "_blank"}>
					{title}
				</a>
			) : (
				<Link className={className} onClick={onClick} to={href}>
					{title}
				</Link>
			)}
		</div>
	);
};

const FooterLinkList = ({ footerListData, row }) => (
	<div className={cls("text-left flex", row?"flex-row md:flex-col gap-3 md:gap-0 justify-center mb-9 md:mb-8 ":"flex-col mb-8")}>
		<p className="text-blue-100 font-medium mb-2">{footerListData.title}</p>
		<div className={cls("flex",row?"flex-row gap-3 md:gap-2":"flex-col")}>{footerListData.items.map((listItem, index) =>
			index < MAX_ITEMS ? <FooterLinkItem key={`${listItem.title}-${index}`} {...listItem} /> : null,
		)}</div>
	</div>
);

export const FooterContent = ({ servicesMenu , showKetch, showSiteInfo, wl_link }) => {
	const { services, workWithUs, promotions, conditionsMenu, instantScriptLinks, help, ISPharmacy, socialLinks } =
		useFooterLists(servicesMenu, showKetch, wl_link);

	return (
		<>
			<div className="bg-is2-blue-850 align-middle">
				<div
					className={
						"mx-auto w-full max-w-[1124px] px-6 xl:px-0 z-10 hidden md:grid grid-cols-4 pt-[60px] pb-11 md:gap-8"
					}
				>
					<div>
						<FooterLinkList footerListData={instantScriptLinks} />
						<FooterLinkList footerListData={ISPharmacy} />
					</div>
					<div>
						<FooterLinkList footerListData={services} />
					</div>
					<div>
						<FooterLinkList footerListData={conditionsMenu} />
					</div>
					<div>
						<FooterLinkList footerListData={promotions} />
						<FooterLinkList footerListData={help} />
						<FooterLinkList footerListData={workWithUs} />
						<FooterLinkList row footerListData={socialLinks} />
					</div>
				</div>
			</div>

			<div className="flex flex-col md:flex-row bg-is2-blue-900 w-100/100 min-h-[57px] px-0 md:px-[2em] xl:px-0 items-center justify-between text-xs text-white md:text-blue-100 font-thin w-full mx-auto max-w-[1124px]">
				<div className="w-full bg-is2-blue-850 md:hidden px-6 mx-auto">
					<div className="flex justify-center pb-6 pt-11">{INSTANT_SCRIPT_LOGO}</div>
					<FooterLinkList row footerListData={socialLinks}/>
				</div>
				<div className="flex flex-col md:flex-row justify-center items-center pt-6 pb-1 px-6 md:p-0 w-full md:w-fit">
					<img
						src="https://storage.googleapis.com/instant-med-public/v2/Wesfarmers_logo_white.svg"
						alt="Wesfarmers"
						className="logo mb-1 md:mb-0 h-[25px] mx-auto"
					/>
					<p className="ml-0 md:ml-3 text-xs">A Wesfarmers company</p>
				</div>
				{showSiteInfo && <div className="order-last md:order-none text-is2-blue-100"><Suspense fallback={null}><SiteInformation /></Suspense></div>}
				<div className="flex items-center justify-center px-6 md:p-0 w-full md:w-fit">
					<p className="uppercase font-thin text-center text-xs">
						© INSTANTSCRIPTS PTY LTD <span className="ml-3">ABN 87 629 120 234</span>
					</p>
				</div>
			</div>
		</>
	);
};
